import { Component, OnInit, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { CardModule } from 'primeng/card';
import { MessageService } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { FileValidationService } from '../../services/file-validation.service';

interface AttachmentField {
  id: number;
  order: number;
  fieldName: string;
  templateFileName: string | null;
  templateFile: File | null;
  required: boolean;
}

@Component({
  selector: 'app-attachment-config',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    CheckboxModule,
    TooltipModule,
    CardModule,
    Toast,
  ],
  providers: [MessageService],
  templateUrl: './attachment-config.component.html',
  styleUrl: './attachment-config.component.scss',
})
export class AttachmentConfigComponent implements OnInit {
  private router = inject(Router);
  private fileValidationService = inject(FileValidationService);
  private messageService = inject(MessageService);

  breadcrumbItems = signal<MenuItem[]>([
    {
      label: 'Configurazione questionari e allegati',
      routerLink: '/axa/questionnaires-and-attachments',
    },
    { label: 'Nuova configurazione allegati' },
  ]);

  // Form fields
  configTitle: string = '';
  selectedCategory: any = null;
  isActive: boolean = true;

  // Table data
  attachmentFields = signal<AttachmentField[]>([]);
  loading = signal<boolean>(false);

  // Dropdown options
  categoryOptions = [
    { label: '-- Categoria fiduciario --', value: null },
    { label: 'Periti Auto', value: 'CAR_SURVEYOR' },
    { label: 'Periti RE', value: 'RE_EXPERT' },
    { label: 'Investigatore', value: 'INVESTIGATOR' },
    { label: 'Medico', value: 'MEDICAL' },
    { label: 'Legale', value: 'LEGAL' },
    { label: 'Carrozzeria', value: 'BODY_SHOP' },
  ];

  ngOnInit(): void {
    this.loadAttachmentFields();
  }

  private loadAttachmentFields(): void {
    this.loading.set(true);

    // Mock data
    const mockData: AttachmentField[] = [
      {
        id: 1,
        order: 1,
        fieldName: 'Foto struttura - Esterni',
        templateFileName: null,
        templateFile: null,
        required: true,
      },
      {
        id: 2,
        order: 2,
        fieldName: 'Conflitto di interessi',
        templateFileName: 'Conflitto di interessi.xlsx',
        templateFile: null,
        required: true,
      },
      {
        id: 3,
        order: 3,
        fieldName: 'Atto di nomina',
        templateFileName: 'Atto di nomina.pdf',
        templateFile: null,
        required: true,
      },
      {
        id: 4,
        order: 4,
        fieldName: 'Questionario sicurezza IT',
        templateFileName: 'Sicurezza IT_cac.pdf',
        templateFile: null,
        required: false,
      },
    ];

    this.attachmentFields.set(mockData);
    this.loading.set(false);
  }

  onAddField(): void {
    const currentFields = this.attachmentFields();
    const newField: AttachmentField = {
      id: currentFields.length + 1,
      order: currentFields.length + 1,
      fieldName: 'Nuovo campo',
      templateFileName: null,
      templateFile: null,
      required: false,
    };
    this.attachmentFields.set([...currentFields, newField]);
  }

  async onFileSelect(event: any, field: AttachmentField): Promise<void> {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // 🔒 Validazione completa del file con FileValidationService
    // Supporta: PDF, DOC, DOCX, XLS, XLSX
    const validation = await this.fileValidationService.validateAttachment(file);
    if (!validation.valid) {
      this.messageService.add({
        severity: 'error',
        summary: '🔒 Validazione File Fallita',
        detail: validation.error || 'Il file non ha superato i controlli di sicurezza',
        life: 6000
      });

      // Log dettagli per debugging
      if (validation.details) {
        console.warn('File validation failed:', validation.details);
      }

      // Reset input
      event.target.value = '';
      return;
    }

    // File valido - aggiorna lo stato
    const currentFields = this.attachmentFields();
    const updatedFields = currentFields.map((f) =>
      f.id === field.id ? { ...f, templateFile: file, templateFileName: file.name } : f,
    );
    this.attachmentFields.set(updatedFields);

    this.messageService.add({
      severity: 'success',
      summary: 'File caricato',
      detail: `Template "${file.name}" caricato con successo`,
      life: 3000
    });
  }

  onRemoveFile(field: AttachmentField): void {
    const currentFields = this.attachmentFields();
    const updatedFields = currentFields.map((f) =>
      f.id === field.id ? { ...f, templateFile: null, templateFileName: null } : f,
    );
    this.attachmentFields.set(updatedFields);
  }

  onEditField(field: AttachmentField): void {
    console.log('Edit field:', field);
    // Implement edit logic
  }

  onDeleteField(field: AttachmentField): void {
    const currentFields = this.attachmentFields();
    const updatedFields = currentFields.filter((f) => f.id !== field.id);
    this.attachmentFields.set(updatedFields);
  }

  onMoveUp(field: AttachmentField): void {
    const currentFields = [...this.attachmentFields()];
    const index = currentFields.findIndex((f) => f.id === field.id);

    if (index > 0) {
      [currentFields[index - 1], currentFields[index]] = [
        currentFields[index],
        currentFields[index - 1],
      ];
      // Update order numbers
      currentFields.forEach((f, i) => (f.order = i + 1));
      this.attachmentFields.set(currentFields);
    }
  }

  onMoveDown(field: AttachmentField): void {
    const currentFields = [...this.attachmentFields()];
    const index = currentFields.findIndex((f) => f.id === field.id);

    if (index < currentFields.length - 1) {
      [currentFields[index], currentFields[index + 1]] = [
        currentFields[index + 1],
        currentFields[index],
      ];
      // Update order numbers
      currentFields.forEach((f, i) => (f.order = i + 1));
      this.attachmentFields.set(currentFields);
    }
  }

  onSave(): void {
    console.log('Saving configuration:', {
      title: this.configTitle,
      category: this.selectedCategory,
      active: this.isActive,
      fields: this.attachmentFields(),
    });
    // Implement save logic
    this.router.navigate(['/axa/questionnaires-and-attachments']);
  }

  onCancel(): void {
    this.router.navigate(['/axa/questionnaires-and-attachments']);
  }
}
